import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface PromoBannerItem {
  id: string;
  text: string;
  link: string | null;
}

interface PromoBannerSettings {
  enabled?: boolean;
  backgroundColor?: string;
}

export const PromoBanner = () => {
  const [banners, setBanners] = useState<PromoBannerItem[]>([]);
  const [isEnabled, setIsEnabled] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState<string>("");

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadBanners();
  }, []);

  useEffect(() => {
    if (banners.length > 1 && !isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, 3000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [banners.length, isPaused]);

  const loadBanners = async () => {
    try {
      const { data: settingData } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "promo_banner_enabled")
        .maybeSingle();

      if (settingData?.setting_value) {
        const value = settingData.setting_value as PromoBannerSettings;
        if (value.enabled === false) {
          setIsEnabled(false);
          setIsLoading(false);
          return;
        }
        if (value.backgroundColor) {
          setBackgroundColor(value.backgroundColor);
        }
      }

      const { data, error } = await supabase
        .from("promo_banners")
        .select("id, text, link")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      setBanners(data || []);
    } catch (error) {
      console.error("Error loading promo banners:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % banners.length);
  };

  if (isLoading || !isEnabled || banners.length === 0) {
    return null;
  }

  const currentBanner = banners[currentIndex];
  const hasMultiple = banners.length > 1;

  return (
    <div
      role="region"
      aria-label="הודעות מבצע"
      aria-live="polite"
      className="bg-primary text-primary-foreground py-2 relative z-[51] flex items-center"
      style={backgroundColor ? { backgroundColor } : undefined}
      data-promo-banner
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="container mx-auto px-4 flex items-center justify-center gap-2">
        {hasMultiple && (
          <button
            onClick={goToPrev}
            className="p-1 hover:bg-primary-foreground/10 rounded-full transition-colors shrink-0"
            aria-label="הודעה קודמת"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}

        <div className="flex-1 min-w-0 text-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex items-center justify-center"
            >
              {currentBanner.link ? (
                <a 
                  href={currentBanner.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm font-medium hover:underline"
                >
                  {currentBanner.text}
                </a>
              ) : (
                <span className="text-sm font-medium">{currentBanner.text}</span>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {hasMultiple && (
          <button
            onClick={goToNext}
            className="p-1 hover:bg-primary-foreground/10 rounded-full transition-colors shrink-0"
            aria-label="הודעה הבאה"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};
